<template>
    <div
        :id="id"
        class="element-FieldWrapper wrapper"
        :tabindex="$slots.toggle ? '0' : '-1'"
        @keyup.enter.self="$slots.toggle ? toggle() : () => {}"
        @keyup.escape="$slots.toggle ? toggle(false) : () => {}"
    >
        <div class="container field" @click="$emit('click')">
            <label
                v-if="labelText"
                class="label"
                :for="id ? `${id}_input` : null"
            >
                {{ labelText }}
            </label>

            <div
                v-if="$slots.toggle"
                :class="{ 'is-open': open, 'is-closed': !open, toggle: true }"
                @click.stop="toggle()"
            >
                <div class="content">
                    <slot name="toggle" />
                </div>
                <div v-if="!alwaysOpen" class="arrow">
                    <font-awesome-icon icon="angle-down" />
                </div>
            </div>

            <div v-else class="content">
                <slot name="default" />
            </div>
        </div>

        <div
            v-if="showError"
            :id="id ? `${id}_error` : null"
            aria-live="polite"
            class="container error"
        >
            {{ errorText }}
        </div>

        <div
            v-if="$slots.toggle && open"
            v-click-outside="close"
            :class="{
                container: true,
                overlay: true,
                content: true,
                floating: floating
            }"
        >
            <slot name="default" />
        </div>
    </div>
</template>

<script>
export default {
    props: {
        id: {
            type: String,
            default: null,
            required: false
        },
        labelText: {
            type: String,
            default: null,
            required: false
        },
        errorText: {
            type: String,
            required: false,
            default: ""
        },
        showError: {
            type: Boolean,
            default: false
        },
        alwaysOpen: {
            type: Boolean,
            default: false
        },
        floating: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            open: this.alwaysOpen
        };
    },
    methods: {
        toggle(force = null) {
            if (!this.alwaysOpen) {
                if (force !== null) {
                    this.open = force;
                } else {
                    this.open = !this.open;
                }

                this.$emit("toggle");
            }
        },
        close() {
            if (!this.alwaysOpen && this.floating && this.open) {
                this.open = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
$element-FieldWrapper-box-shadow: none !default;
$element-FieldWrapper-container-padding: 0.8em !default;
$element-FieldWrapper-label-font: 600 0.9rem $font-family !default;
$element-FieldWrapper-label-color: $color-primary !default;
$element-FieldWrapper-background: #fff !default;
$element-FieldWrapper-overlay-background: $element-FieldWrapper-background !default;
$element-FieldWrapper-overlay-margin-top: 2px !default;

.wrapper {
    position: relative;
    box-shadow: $element-FieldWrapper-box-shadow;
}

.wrapper:focus-within {
    outline: 1px solid $color-primary;
}

.container {
    padding: $element-FieldWrapper-container-padding;
    background: $element-FieldWrapper-background;
}

.field {
    display: flex;
    flex-direction: column;
}

.field .label {
    font: $element-FieldWrapper-label-font;
    color: $element-FieldWrapper-label-color;
    padding-bottom: 5px;
}

.error {
    color: red;
    font-size: 0.8em;
    margin-top: 1px;
}

.toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font: $input-font;
    color: $input-color;
    line-height: 1.6rem;
}

.toggle.is-open .arrow {
    transition: transform 0.2s ease, -webkit-transform 0.2s ease;
    transform: rotateZ(180deg);
}

.toggle.is-closed .arrow {
    transition: transform 0.2s ease, -webkit-transform 0.2s ease;
    transform: rotateZ(0deg);
}

.overlay {
    margin-top: $element-FieldWrapper-overlay-margin-top;

    /* only relevant is overlay is set position: absolute from the outside
     * if we want to make it a real overlay */
    width: 100%;
    box-sizing: border-box;
    z-index: 1;
    background: $element-FieldWrapper-overlay-background;
}

.overlay.floating {
    position: absolute;
    box-shadow: $element-FieldWrapper-box-shadow;
}
</style>
